import { useMemo, useState } from 'react'
import { MAHARAJ_TIERS, daysBetween, isAllowedDaysUntil, maxVisibilityDays } from './maharajPricingData'
import TimeSlotPicker from './TimeSlotPicker'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const TIER_ICON_PATHS = {
  zap: ['M13 2 3 14h9l-1 8 10-12h-9l1-8Z'],
  star: [
    'M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z',
  ],
  thumb: [
    'M7 10v12',
    'M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z',
  ],
}

function TierIcon({ icon, className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {TIER_ICON_PATHS[icon].map((d) => (
        <path key={d} strokeLinecap="round" strokeLinejoin="round" d={d} />
      ))}
    </svg>
  )
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export default function MaharajScheduleSection({ astrologerId, selectedDate, selectedSlot, activeTierKey, onSelectDate, onSelectSlot, tiers = MAHARAJ_TIERS }) {
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])
  const maxVisibleDate = useMemo(() => {
    const d = new Date(today)
    d.setDate(d.getDate() + maxVisibilityDays(tiers))
    return d
  }, [today, tiers])

  const [viewMonth, setViewMonth] = useState(today.getMonth())
  const [viewYear, setViewYear] = useState(today.getFullYear())

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay()
  const isCurrentMonth = viewMonth === today.getMonth() && viewYear === today.getFullYear()
  const isMaxMonth = viewMonth === maxVisibleDate.getMonth() && viewYear === maxVisibleDate.getFullYear()

  const goPrevMonth = () => {
    if (isCurrentMonth) return
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1) } else { setViewMonth((m) => m - 1) }
  }
  const goNextMonth = () => {
    if (isMaxMonth) return
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1) } else { setViewMonth((m) => m + 1) }
  }

  const handlePickDay = (day) => {
    onSelectDate(new Date(viewYear, viewMonth, day))
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 2v4M16 2v4" />
            <rect width="18" height="18" x="3" y="4" rx="2" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18" />
          </svg>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#8B7355]">Schedule System</span>
        </div>
        <span className="hero-card-badge">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4M12 8h.01" />
          </svg>
          45-Day Visibility Point
        </span>
      </div>

      <div className="info-card overflow-hidden">
        <div className="h-0.75 w-full bg-linear-to-r from-[#C8922A] via-[#E5A820] to-[#C8922A]" />
        <div className="p-6 sm:p-7 space-y-6">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8B7355]">Pricing system area (max 45 days)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {tiers.map((tier) => {
              const isActive = activeTierKey === tier.key
              return (
                <div
                  key={tier.key}
                  className="relative rounded-2xl p-4 pt-5 border transition-all hover:-translate-y-1"
                  style={
                    isActive
                      ? { borderColor: 'rgba(242,160,7,.5)', background: 'rgba(242,160,7,.07)', boxShadow: '0 14px 30px -10px rgba(212,175,55,.4)' }
                      : { borderColor: 'rgba(215,194,154,.3)', background: '#fff', boxShadow: '0 4px 14px -8px rgba(28,20,10,.12)' }
                  }
                >
                  {tier.recommended && (
                    <span className="feat-ribbon">Most Chosen</span>
                  )}
                  <div className="flex items-center gap-2.5 mb-3">
                    <span
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#1C140A]"
                      style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F7D08F 100%)' }}
                    >
                      <TierIcon icon={tier.icon} className="w-4 h-4" />
                    </span>
                    <span className="text-[11px] font-bold uppercase tracking-wide text-[#1C140A]">{tier.label}</span>
                  </div>
                  <p className="text-2xl font-cinzel font-bold text-gradient-gold">₹{tier.price.toLocaleString('en-IN')}</p>
                  <p className="text-[10px] text-[#8B7355] mt-1 uppercase tracking-wide">{tier.minDays}-{tier.maxDays} days</p>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className="info-card overflow-hidden">
        <div className="h-0.75 w-full bg-linear-to-r from-[#C8922A] via-[#E5A820] to-[#C8922A]" />
        <div className="p-6 border-b border-[#D7C29A]/20 bg-linear-to-r from-[#FFFCF5] to-[#FEF6E4] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              className="hidden sm:inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#1C140A]"
              style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F7D08F 100%)' }}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 2v4M16 2v4" />
                <rect width="18" height="18" x="3" y="4" rx="2" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18" />
              </svg>
            </span>
            <div className="flex flex-col">
              <span className="text-[10px] font-mono font-normal text-secondary uppercase tracking-[0.3em] mb-1">Celestial View</span>
              <h4 className="text-xl font-cinzel text-[#1C140A]">{MONTH_NAMES[viewMonth]} {viewYear}</h4>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={goPrevMonth}
              disabled={isCurrentMonth}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-[#D7C29A]/30 text-[#1C140A]/50 transition-all hover:scale-105 hover:border-transparent hover:bg-linear-to-br hover:from-[#D4AF37] hover:to-[#F7D08F] hover:text-[#1C140A] hover:shadow-md disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-none disabled:hover:border-[#D7C29A]/30 disabled:hover:text-[#1C140A]/50"
              aria-label="Previous month"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={goNextMonth}
              disabled={isMaxMonth}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-[#D7C29A]/30 text-[#1C140A]/50 transition-all hover:scale-105 hover:border-transparent hover:bg-linear-to-br hover:from-[#D4AF37] hover:to-[#F7D08F] hover:text-[#1C140A] hover:shadow-md disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-none disabled:hover:border-[#D7C29A]/30 disabled:hover:text-[#1C140A]/50"
              aria-label="Next month"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-7 gap-1 mb-3 rounded-lg py-1.5" style={{ background: 'rgba(200,146,42,.05)' }}>
            {WEEKDAYS.map((d, i) => (
              <div
                key={d}
                className="text-center text-[9px] font-bold uppercase tracking-widest py-1"
                style={{ color: i === 0 || i === 6 ? '#C8922A' : '#8B7355' }}
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {Array.from({ length: firstWeekday }, (_, i) => <div key={`pad-${i}`} aria-hidden="true" />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
              const cellDate = new Date(viewYear, viewMonth, day)
              const isPast = cellDate < today
              const isBeyondVisibility = cellDate > maxVisibleDate
              const isOutsideSlab = !isPast && !isBeyondVisibility && !isAllowedDaysUntil(daysBetween(today, cellDate), tiers)
              const isDisabled = isPast || isBeyondVisibility || isOutsideSlab
              const isToday = isSameDay(cellDate, today)
              const isActive = selectedDate ? isSameDay(cellDate, selectedDate) : false
              const dayOfWeek = cellDate.getDay()
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

              let cls = 'relative aspect-square rounded-xl flex items-center justify-center text-xs font-semibold transition-all duration-200 '
              if (isActive) cls += 'text-white shadow-[0_8px_18px_-6px_rgba(28,20,10,.55),0_0_0_3px_rgba(242,160,7,.22)] scale-[1.04]'
              else if (isDisabled) cls += `cursor-not-allowed ${isBeyondVisibility ? 'text-[#1C140A]/15' : 'text-[#1C140A]/20'}`
              else if (isToday) cls += 'calendar-today-ring border border-secondary text-[#1C140A] hover:bg-secondary hover:text-white hover:scale-105'
              else cls += `text-[#1C140A]/75 hover:bg-secondary/15 hover:text-[#1C140A] hover:scale-105 hover:shadow-sm ${isWeekend ? 'bg-secondary/[0.04]' : ''}`

              return (
                <button
                  key={day}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handlePickDay(day)}
                  className={cls}
                  style={isActive ? { background: 'linear-gradient(135deg, #1C140A 0%, #2E1206 100%)' } : undefined}
                >
                  {day}
                  {isToday && !isActive && <span className="absolute bottom-1 w-1 h-1 rounded-full bg-secondary" />}
                  {isBeyondVisibility && !isPast && (
                    <svg className="absolute top-0.5 right-0.5 w-2 h-2 text-[#1C140A]/25" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Zm0 2a3 3 0 0 1 3 3v3H9V6a3 3 0 0 1 3-3Z" />
                    </svg>
                  )}
                </button>
              )
            })}
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-6 pt-5 border-t border-[#D7C29A]/15">
            <span className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-[#8B7355]">
              <span className="w-2.5 h-2.5 rounded-full border border-secondary" /> Today
            </span>
            <span className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-[#8B7355]">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'linear-gradient(135deg,#1C140A,#2E1206)' }} /> Selected
            </span>
            <span className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-[#8B7355]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#1C140A]/10" /> Unavailable
            </span>
          </div>
        </div>
      </div>

      <TimeSlotPicker
        astrologerId={astrologerId}
        selectedDate={selectedDate}
        selectedSlot={selectedSlot}
        onSelectSlot={onSelectSlot}
      />
    </div>
  )
}
